/**
 * Keyboard navigation arithmetic for dropdown-style option lists.
 *
 * Consumers own their own `highlightedIndex` ($state) and build a `NavRow[]`
 * describing what is actually rendered — including non-selectable rows such as
 * dividers — so index numbering never needs offset correction.
 */

export enum NavRowKind {
  Item = 'Item',
  Divider = 'Divider',
  Create = 'Create'
}

export type NavRow<T = CommonList> = {
  kind: NavRowKind;
  /** Always present, so consumers never have to narrow. Synthetic rows
   *  (a "create" affordance) pass a placeholder. */
  item: T;
  selectable: boolean;
};

export const NoHighlight = -1;

/** Indices of the rows that can hold the highlight. */
export const getSelectableIndices = (rows: NavRow[]): number[] =>
  rows.reduce<number[]>((acc, row, index) => {
    if (row.selectable) {
      acc.push(index);
    }
    return acc;
  }, []);

export const firstSelectable = (selectable: number[]): number =>
  selectable.length ? selectable[0] : NoHighlight;

export const lastSelectable = (selectable: number[]): number =>
  selectable.length ? selectable[selectable.length - 1] : NoHighlight;

/** Move the highlight one step, wrapping at both ends. */
export const moveHighlight = (current: number, selectable: number[], dir: 1 | -1): number => {
  if (!selectable.length) {
    return NoHighlight;
  }
  const position = selectable.indexOf(current);
  if (position === -1) {
    return dir === 1 ? firstSelectable(selectable) : lastSelectable(selectable);
  }
  const next = (position + dir + selectable.length) % selectable.length;
  return selectable[next];
};

/** True for keys that should feed the type-ahead buffer. */
export const isTypeAheadKey = (e: KeyboardEvent): boolean =>
  e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey;

const TypeAheadResetMs = 500;

/**
 * Native `<select>`-style type-ahead: typing "ca" jumps to the first option
 * starting with "ca". The buffer clears after a pause.
 */
export const createTypeAhead = (resetMs: number = TypeAheadResetMs) => {
  let buffer = '';
  let timer: ReturnType<typeof setTimeout> | null = null;

  const reset = (): void => {
    buffer = '';
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  /** Returns the row index to highlight, or NoHighlight when nothing matches. */
  const match = (key: string, rows: NavRow[], from: number): number => {
    buffer += key.toLowerCase();
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(reset, resetMs);

    // A repeated single character cycles through options starting with it.
    const isRepeat = buffer.length > 1 && buffer.split('').every((c) => c === buffer[0]);
    const prefix = isRepeat ? buffer[0] : buffer;
    // On a fresh buffer (or a repeat) start looking after the current row.
    const startAfter = buffer.length === 1 || isRepeat ? from : from - 1;

    for (let offset = 1; offset <= rows.length; offset++) {
      const index = (startAfter + offset + rows.length) % rows.length;
      const row = rows[index];
      if (row?.selectable && row.item?.label?.toLowerCase().startsWith(prefix)) {
        return index;
      }
    }
    return NoHighlight;
  };

  return { match, reset };
};

/**
 * Keep the highlighted row visible by adjusting the scroll container directly.
 * Deliberately not `Element.scrollIntoView` — that can scroll page ancestors,
 * which detaches the fixed-position (`hasOverflow`) dropdown from its trigger.
 */
export const scrollRowIntoView = (container: HTMLElement | undefined, index: number): void => {
  const row = container?.querySelector<HTMLElement>(`[data-nav-index="${index}"]`);
  if (!container || !row) {
    return;
  }
  const top = row.offsetTop;
  const bottom = top + row.offsetHeight;
  if (top < container.scrollTop) {
    container.scrollTop = top;
  } else if (bottom > container.scrollTop + container.clientHeight) {
    container.scrollTop = bottom - container.clientHeight;
  }
};
